<template>
  <div class="kyc-layout">
    <!-- SIDEBAR -->
    <div class="kyc-sidebar">
      <div class="kyc-logo">
        KYC demo
      </div>
      <div class="kyc-steps">
        <div
          v-for="step in steps"
          :key="step.id"
          class="kyc-step"
          :class="{
            active: currentStep === step.id,
            completed: step.id < currentStep && currentStep <= 6,
            failed: step.id === 6 && scenario === 'fail' && currentStep === 6,
          }"
          :data-step="step.id"
        >
          <div class="kyc-step-dot">
            <span v-if="step.id < currentStep && currentStep <= 6 && !(step.id === 6 && scenario === 'fail')">✓</span>
            <span v-else-if="step.id === 6 && scenario === 'fail' && currentStep === 6">✗</span>
            <span v-else>{{ step.id }}</span>
          </div>
          <div class="kyc-step-info">
            <h4>{{ step.title }}</h4>
            <p>{{ step.subtitle }}</p>
          </div>
        </div>
      </div>
      <div class="kyc-exit">
        <a href="user">← Exit</a>
      </div>
    </div>

    <!-- MAIN -->
    <div class="kyc-main">
      <!-- STEP 1 (originally step 2): Identity Verification -->
      <div
        id="step-1"
        class="kyc-step-content"
        :class="{ active: currentStep === 1 }"
      >
        <div class="kyc-card">
          <div class="kyc-card-title">
            Identity Verification
          </div>
          <div class="kyc-card-sub">
            Upload a government-issued ID and complete liveness verification. Powered by Shufti Pro.
          </div>

          <div class="form-group">
            <label class="form-label">Document type</label>
            <select
              v-model="identity.documentType"
              class="form-select"
            >
              <option>Passport</option>
              <option>Emirates ID</option>
              <option>National ID Card</option>
              <option>Driving License</option>
            </select>
          </div>

          <div
            class="form-grid"
            style="margin-bottom:16px"
          >
            <div>
              <label class="form-label">Front side</label>
              <div
                class="upload-area"
                :class="{ 'has-file': identity.frontUploaded }"
                @click="simulateUpload('front')"
              >
                <div class="upload-icon">
                  {{ identity.frontUploaded ? '✅' : '📄' }}
                </div>
                <div
                  class="upload-text"
                  v-html="identity.frontUploaded ? '<strong>File uploaded successfully</strong>' : 'Click to upload or <strong>drag & drop</strong>'"
                />
                <div class="upload-file-name">
                  {{ identity.frontUploaded ? 'document_scan.pdf (2.4 MB)' : '' }}
                </div>
              </div>
            </div>
            <div>
              <label class="form-label">Back side (if applicable)</label>
              <div
                class="upload-area"
                :class="{ 'has-file': identity.backUploaded }"
                @click="simulateUpload('back')"
              >
                <div class="upload-icon">
                  {{ identity.backUploaded ? '✅' : '📄' }}
                </div>
                <div
                  class="upload-text"
                  v-html="identity.backUploaded ? '<strong>File uploaded successfully</strong>' : 'Click to upload or <strong>drag & drop</strong>'"
                />
                <div class="upload-file-name">
                  {{ identity.backUploaded ? 'document_scan.pdf (2.4 MB)' : '' }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Proof of Address</label>
            <div
              class="upload-area"
              :class="{ 'has-file': identity.poaUploaded }"
              @click="simulateUpload('poa')"
            >
              <div class="upload-icon">
                {{ identity.poaUploaded ? '✅' : '🏠' }}
              </div>
              <div
                class="upload-text"
                v-html="identity.poaUploaded ? '<strong>File uploaded successfully</strong>' : 'Utility bill or bank statement (last 3 months)'"
              />
              <div class="upload-file-name">
                {{ identity.poaUploaded ? 'document_scan.pdf (2.4 MB)' : '' }}
              </div>
            </div>
          </div>

          <div style="margin-top:24px">
            <label class="form-label">Liveness Check</label>
            <div class="liveness-box">
              <div class="liveness-circle">
                📷
              </div>
              <div class="liveness-text">
                Position your face within the circle
              </div>
              <div class="liveness-sub">
                Ensure good lighting. Remove glasses if possible.
              </div>
              <button
                class="btn btn-outline"
                style="margin-top:16px"
                @click="completeLiveness"
              >
                {{ cameraText }}
              </button>
            </div>
          </div>

          <div style="margin-top:28px;display:flex;gap:12px;justify-content:center;">
            <button
              class="btn btn-primary"
              @click="nextStep"
            >
              Continue →
            </button>
          </div>
        </div>
      </div>

      <!-- STEP 2 (originally step 4): Source of Funds -->
      <div
        id="step-2"
        class="kyc-step-content"
        :class="{ active: currentStep === 2 }"
      >
        <div class="kyc-card">
          <div class="kyc-card-title">
            Source of Funds
          </div>
          <div class="kyc-card-sub">
            Required under AML regulations. We need to verify the origin of your investment funds.
          </div>

          <div class="form-group">
            <label class="form-label">Primary source of funds</label>
            <select
              v-model="funds.primarySource"
              class="form-select"
            >
              <option selected>
                Employment salary
              </option>
              <option>Business income</option>
              <option>Investment returns</option>
              <option>Savings</option>
              <option>Inheritance</option>
              <option>Gift</option>
              <option>Property sale</option>
              <option>Other</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Describe the source of your wealth</label>
            <textarea
              v-model="funds.wealthDescription"
              class="form-input"
              rows="3"
              placeholder="e.g., Accumulated savings from 10+ years of employment in technology sector..."
            />
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">Country of tax residence</label>
              <select
                v-model="funds.taxCountry"
                class="form-select"
              >
                <option>United Arab Emirates</option>
                <option>Saudi Arabia</option>
                <option selected>
                  Montenegro
                </option>
                <option>Other</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Bank name</label>
              <input
                v-model="funds.bankName"
                type="text"
                class="form-input"
                placeholder="Your primary bank"
              >
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Are you a Politically Exposed Person (PEP)?</label>
            <div class="radio-group">
              <label class="radio-option">
                <input
                  v-model="funds.pep"
                  type="radio"
                  name="pep"
                  value="no"
                > <label>No, I am not a PEP</label>
              </label>
              <label class="radio-option">
                <input
                  v-model="funds.pep"
                  type="radio"
                  name="pep"
                  value="yes"
                > <label>Yes, I am a PEP or a close associate of a PEP</label>
              </label>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Supporting documents (optional)</label>
            <div
              class="upload-area"
              :class="{ 'has-file': funds.supportingDocs }"
              @click="simulateUpload('supporting')"
            >
              <div class="upload-icon">
                {{ funds.supportingDocs ? '✅' : '📎' }}
              </div>
              <div
                class="upload-text"
                v-html="funds.supportingDocs ? '<strong>File uploaded successfully</strong>' : 'Bank statements, payslips, or other evidence'"
              />
              <div class="upload-file-name">
                {{ funds.supportingDocs ? 'document_scan.pdf (2.4 MB)' : '' }}
              </div>
            </div>
          </div>

          <div class="kyc-card-footer">
            <button
              class="btn btn-outline"
              @click="prevStep"
            >
              ← Back
            </button>
            <button
              class="btn btn-primary"
              @click="submitForVerification"
            >
              Submit for Verification →
            </button>
          </div>
        </div>
      </div>

      <!-- STEP 3 (originally step 5): AML Screening -->
      <div
        id="step-3"
        class="kyc-step-content"
        :class="{ active: currentStep === 3 }"
      >
        <div class="kyc-card">
          <div class="processing">
            <div class="processing-spinner" />
            <div class="processing-title">
              Verifying your identity...
            </div>
            <div class="processing-sub">
              This usually takes 30-60 seconds. Please don't close this page.
            </div>
            <div class="processing-steps">
              <div
                v-for="check in amlChecks"
                :key="check.id"
                class="processing-step"
                :class="{ active: check.status === 'active', done: check.status === 'done' }"
                :style="check.status === 'failed' ? { color: '#e53935' } : {}"
              >
                <div class="processing-step-icon">
                  <span v-if="check.status === 'pending'">⏳</span>
                  <span v-else-if="check.status === 'active'">⏳</span>
                  <span v-else-if="check.status === 'done'">✅</span>
                  <span v-else-if="check.status === 'failed'">❌</span>
                  <span v-else-if="check.status === 'skipped'">⏭️</span>
                </div>
                <span>{{ check.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 4 (originally step 6): Result -->
      <div
        id="step-4"
        class="kyc-step-content"
        :class="{ active: currentStep === 4 }"
      >
        <div
          v-if="scenario === 'pass'"
          class="kyc-card"
          style="text-align:center;"
        >
          <div class="result-icon success">
            ✓
          </div>
          <div
            class="result-title"
            style="color:#2e7d32"
          >
            Identity Verified
          </div>
          <div class="result-sub">
            Your KYC verification is complete. You can now start investing on RWA.Capital.
          </div>
          <div
            class="result-details"
            style="text-align:left;"
          >
            <div class="result-row">
              <span class="label">Status</span><span class="value green">Verified</span>
            </div>
            <div class="result-row">
              <span class="label">Investor Type</span><span class="value">Retail Client</span>
            </div>
            <div class="result-row">
              <span class="label">Annual Limit</span><span class="value">183,625 AED ($50,000)</span>
            </div>
            <div class="result-row">
              <span class="label">Per-Asset Limit</span><span class="value">183,625 AED ($50,000)</span>
            </div>
            <div class="result-row">
              <span class="label">Max Ownership</span><span class="value">33% per property</span>
            </div>
            <div class="result-row">
              <span class="label">Verification ID</span><span class="value">#KYC-2026-{{ randomId }}</span>
            </div>
            <div class="result-row">
              <span class="label">Provider</span><span class="value">Shufti Pro</span>
            </div>
            <div class="result-row">
              <span class="label">Valid until</span><span class="value">Feb 19, 2027</span>
            </div>
          </div>
          <div style="margin-top:28px;display:flex;gap:12px;justify-content:center;">
            <div
              class="btn btn-primary btn-lg"
              @click="goToMarketplace"
            >
              Go to Marketplace →
            </div>
          </div>
        </div>

        <div
          v-else
          class="kyc-card"
          style="text-align:center;"
        >
          <div class="result-icon fail">
            ✗
          </div>
          <div
            class="result-title"
            style="color:#c62828"
          >
            Verification Failed
          </div>
          <div class="result-sub">
            We were unable to verify your identity. Please review the details below.
          </div>
          <div
            class="result-details"
            style="text-align:left;"
          >
            <div class="result-row">
              <span class="label">Status</span><span class="value red">Failed</span>
            </div>
            <div class="result-row">
              <span class="label">Reason</span><span class="value red">Document quality insufficient</span>
            </div>
            <div class="result-row">
              <span class="label">Details</span><span class="value">The uploaded document image was blurry or partially obscured. OCR could not extract required fields.</span>
            </div>
            <div class="result-row">
              <span class="label">Failed Check</span><span class="value red">Document OCR & validation</span>
            </div>
            <div class="result-row">
              <span class="label">Attempt</span><span class="value">#KYC-2026-{{ randomId }}</span>
            </div>
          </div>
          <div style="margin-top:28px;display:flex;gap:12px;justify-content:center;">
            <button
              class="btn btn-primary btn-lg"
              @click="resetKYC"
            >
              Try Again
            </button>
            <a
              href="mailto:support@rwa.capital"
              class="btn btn-outline btn-lg"
            >Contact Support</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Scenario Toggle -->
    <button
      class="scenario-toggle"
      @click="toggleScenario"
    >
      <span
        class="dot"
        :class="scenario"
      />
      <span>{{ scenario === 'pass' ? 'Pass KYC' : 'Fail KYC' }}</span>
    </button>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "KYCVerification",
  data() {
    return {
      currentStep: 1,
      scenario: "pass", // 'pass' or 'fail'
      cameraText: "Start camera",
      steps: [
        { id: 1, title: "Identity Verification", subtitle: "Upload ID & liveness check" },
        { id: 2, title: "Source of Funds", subtitle: "AML compliance" },
        { id: 3, title: "AML Screening", subtitle: "Automated checks" },
        { id: 4, title: "Result", subtitle: "Verification outcome" }
      ],
      identity: {
        documentType: "Passport",
        frontUploaded: false,
        backUploaded: false,
        poaUploaded: false,
        livenessComplete: false
      },
      funds: {
        primarySource: "Employment salary",
        wealthDescription: "Accumulated savings from employment in technology sector over 8 years. Monthly salary with additional annual bonuses.",
        taxCountry: "Montenegro",
        bankName: "Emirates NBD",
        pep: "no",
        supportingDocs: false
      },
      amlChecks: [
        { id: "ocr", label: "Document OCR & validation", status: "pending" },
        { id: "ofac", label: "OFAC sanctions list", status: "pending" },
        { id: "eu", label: "EU sanctions list", status: "pending" },
        { id: "un", label: "UN sanctions list", status: "pending" },
        { id: "uae", label: "UAE national sanctions", status: "pending" },
        { id: "pep", label: "PEP database check", status: "pending" },
        { id: "report", label: "Generating compliance report", status: "pending" }
      ],
      randomId: Math.floor(Math.random() * 9000 + 1000)
    };
  },
  methods: {
    ...mapActions({
      fakeKYCSuccess: "profile/fakeKYCSuccess"
    }),
    async goToMarketplace() {
      try {
        const resp = await this.fakeKYCSuccess();
        if (resp.status === 267) {
          this.$toast.error("KYC error");
        }
        else {
          this.$toast.success(this.$t("kyc.success"));
          this.$router.push({ name: "projects" });
        }
      }
      catch (e) {
        this.$toast.error("KYC error");
      }
    },
    nextStep() {
      if (this.currentStep < 4) {
        this.goToStep(this.currentStep + 1);
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.goToStep(this.currentStep - 1);
      }
    },
    goToStep(step) {
      this.currentStep = step;
      if (step === 3) {
        this.runScreening();
      }
    },
    simulateUpload(type) {
      if (type === "front") {
        this.identity.frontUploaded = true;
      }
      if (type === "back") {
        this.identity.backUploaded = true;
      }
      if (type === "poa") {
        this.identity.poaUploaded = true;
      }
      if (type === "supporting") {
        this.funds.supportingDocs = true;
      }
    },
    completeLiveness() {
      this.identity.livenessComplete = true;
      this.cameraText = "✓ Capture complete";
    },
    submitForVerification() {
      this.goToStep(3);
    },
    runScreening() {
      // Reset AML checks
      this.amlChecks.forEach(check => {
        check.status = "pending";
      });

      let i = 0;
      const nextCheck = () => {
        if (i >= this.amlChecks.length) {
          setTimeout(() => {
            this.goToStep(4);
          }, 500);
          return;
        }

        const currentCheck = this.amlChecks[i];
        currentCheck.status = "active";

        setTimeout(() => {
          if (this.scenario === "fail" && currentCheck.id === "ocr") {
            currentCheck.status = "failed";
            // Mark remaining as skipped
            for (let j = i + 1; j < this.amlChecks.length; j++) {
              this.amlChecks[j].status = "skipped";
            }
            setTimeout(() => {
              this.goToStep(4);
            }, 1000);
            return;
          }

          currentCheck.status = "done";
          i++;
          nextCheck();
        }, 600 + Math.random() * 400);
      };

      nextCheck();
    },
    resetKYC() {
      // Reset all steps
      this.currentStep = 1;
      this.identity = {
        documentType: "Passport",
        frontUploaded: false,
        backUploaded: false,
        poaUploaded: false,
        livenessComplete: false
      };
      this.funds = {
        primarySource: "Employment salary",
        wealthDescription: "Accumulated savings from employment in technology sector over 8 years. Monthly salary with additional annual bonuses.",
        taxCountry: "Montenegro",
        bankName: "Emirates NBD",
        pep: "no",
        supportingDocs: false
      };
      this.amlChecks.forEach(check => {
        check.status = "pending";
      });
      this.randomId = Math.floor(Math.random() * 9000 + 1000);
    },
    toggleScenario() {
      this.scenario = this.scenario === "pass" ? "fail" : "pass";
    }
  }
};
</script>

<style scoped>
/* Copy all the styles from the original <style> block here */
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Inter',sans-serif;background:#f5f5f5;color:#1a1a2e;min-height:100vh;display:flex}

.kyc-layout{display:flex;width:100%;min-height:100vh}

/* Sidebar */
.kyc-sidebar{width:280px;background:#fff;border-right:1px solid #eee;padding:24px;display:flex;flex-direction:column;position:fixed;top:0;left:0;bottom:0;z-index:10}
.kyc-logo{font-size:18px;font-weight:800;color:#1a1a2e;margin-bottom:32px}
.kyc-logo span{color:#2d8c6e}
.kyc-steps{flex:1}
.kyc-step{display:flex;align-items:flex-start;gap:12px;padding:12px 0;position:relative}
.kyc-step+.kyc-step::before{content:'';position:absolute;left:15px;top:-4px;width:2px;height:16px;background:#eee}
.kyc-step-dot{width:32px;height:32px;border-radius:50%;border:2px solid #ddd;background:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#999;flex-shrink:0;transition:all 0.3s}
.kyc-step.active .kyc-step-dot{border-color:#2d8c6e;color:#2d8c6e}
.kyc-step.completed .kyc-step-dot{border-color:#2d8c6e;background:#2d8c6e;color:#fff}
.kyc-step.failed .kyc-step-dot{border-color:#e53935;background:#e53935;color:#fff}
.kyc-step-info h4{font-size:13px;font-weight:600;color:#999;transition:color 0.3s}
.kyc-step.active .kyc-step-info h4{color:#1a1a2e}
.kyc-step.completed .kyc-step-info h4{color:#2d8c6e}
.kyc-step.failed .kyc-step-info h4{color:#e53935}
.kyc-step-info p{font-size:11px;color:#bbb}
.kyc-exit{margin-top:auto;padding-top:20px;border-top:1px solid #eee}
.kyc-exit a{font-size:13px;color:#999;text-decoration:none;display:flex;align-items:center;gap:6px}
.kyc-exit a:hover{color:#e53935}

/* Main */
.kyc-main{margin-left:280px;flex:1;display:flex;align-items:center;justify-content:center;padding:40px}
.kyc-card{background:#fff;border-radius:12px;border:1px solid #e5e5e5;padding:40px;max-width:640px;width:100%;box-shadow:0 2px 12px rgba(0,0,0,0.04)}
.kyc-card-title{font-size:22px;font-weight:700;color:#1a1a2e;margin-bottom:4px}
.kyc-card-sub{font-size:14px;color:#777;margin-bottom:28px}
.kyc-card-footer{display:flex;justify-content:space-between;align-items:center;margin-top:32px;padding-top:20px;border-top:1px solid #eee}

/* Form elements */
.form-group{margin-bottom:16px}
.form-label{display:block;font-size:13px;font-weight:600;color:#333;margin-bottom:6px}
.form-input,.form-select{width:100%;padding:10px 14px;border:1px solid #ddd;border-radius:8px;font-size:14px;color:#333;background:#fff;transition:all 0.2s}
.form-input:focus,.form-select:focus{outline:none;border-color:#2d8c6e;box-shadow:0 0 0 3px rgba(45,140,110,0.1)}
.form-select{appearance:auto}
.form-hint{font-size:11px;color:#999;margin-top:4px}
.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}

.btn{display:inline-flex;align-items:center;gap:6px;padding:10px 20px;border-radius:8px;font-size:14px;font-weight:600;border:none;cursor:pointer;transition:all 0.2s;text-decoration:none}
.btn-primary{background:#2d8c6e;color:#fff}
.btn-primary:hover{background:#24745b}
.btn-outline{background:#fff;color:#555;border:1px solid #ddd}
.btn-outline:hover{border-color:#999}
.btn-danger{background:#e53935;color:#fff}
.btn-danger:hover{background:#c62828}
.btn-lg{padding:12px 28px;font-size:15px}
.btn:disabled{opacity:0.5;cursor:not-allowed}

/* Checkbox list */
.check-list{display:flex;flex-direction:column;gap:12px}
.check-item{display:flex;align-items:flex-start;gap:10px;padding:12px 16px;border:1px solid #eee;border-radius:8px;cursor:pointer;transition:all 0.2s}
.check-item:hover{border-color:#2d8c6e}
.check-item.checked{border-color:#2d8c6e;background:#f0faf5}
.check-item input{margin-top:2px;accent-color:#2d8c6e}
.check-item label{font-size:13px;color:#555;line-height:1.5;cursor:pointer}

/* Upload area */
.upload-area{border:2px dashed #ddd;border-radius:8px;padding:32px;text-align:center;cursor:pointer;transition:all 0.2s}
.upload-area:hover{border-color:#2d8c6e;background:#f0faf5}
.upload-area.has-file{border-color:#2d8c6e;background:#f0faf5;border-style:solid}
.upload-icon{font-size:32px;margin-bottom:8px}
.upload-text{font-size:13px;color:#777}
.upload-text strong{color:#2d8c6e}
.upload-file-name{font-size:12px;color:#2d8c6e;font-weight:600;margin-top:4px}

/* Liveness */
.liveness-box{background:#f8f8f8;border:1px solid #eee;border-radius:12px;padding:32px;text-align:center;position:relative;overflow:hidden}
.liveness-circle{width:120px;height:120px;border-radius:50%;background:#eee;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;font-size:48px;position:relative}
.liveness-circle::after{content:'';position:absolute;inset:-4px;border-radius:50%;border:2px solid transparent;border-top-color:#2d8c6e;animation:spin 1.5s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.liveness-text{font-size:14px;font-weight:600;color:#1a1a2e}
.liveness-sub{font-size:12px;color:#999}

/* Radio options */
.radio-group{display:flex;flex-direction:column;gap:8px}
.radio-option{display:flex;align-items:center;gap:10px;padding:10px 14px;border:1px solid #eee;border-radius:8px;cursor:pointer;transition:all 0.2s}
.radio-option:hover{border-color:#2d8c6e}
.radio-option input{accent-color:#2d8c6e}
.radio-option label{font-size:13px;color:#555;cursor:pointer}

/* Slider */
.range-slider{width:100%;margin:8px 0;accent-color:#2d8c6e}
.range-labels{display:flex;justify-content:space-between;font-size:11px;color:#999}

/* Processing animation */
.processing{text-align:center;padding:40px 0}
.processing-spinner{width:60px;height:60px;border:3px solid #eee;border-top-color:#2d8c6e;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 20px}
.processing-title{font-size:18px;font-weight:700;color:#1a1a2e;margin-bottom:8px}
.processing-sub{font-size:13px;color:#777;margin-bottom:24px}
.processing-steps{text-align:left;max-width:320px;margin:0 auto}
.processing-step{display:flex;align-items:center;gap:10px;padding:8px 0;font-size:13px;color:#bbb;transition:all 0.5s}
.processing-step.active{color:#1a1a2e}
.processing-step.done{color:#2d8c6e}
.processing-step-icon{width:20px;text-align:center;font-size:14px}

/* Result */
.result-icon{width:80px;height:80px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:36px;margin:0 auto 20px}
.result-icon.success{background:#e8f5e9;color:#2e7d32}
.result-icon.fail{background:#ffebee;color:#c62828}
.result-title{font-size:22px;font-weight:700;text-align:center;margin-bottom:8px}
.result-sub{font-size:14px;color:#777;text-align:center;margin-bottom:28px}
.result-details{background:#f8f9fa;border-radius:8px;padding:16px}
.result-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee;font-size:13px}
.result-row:last-child{border-bottom:none}
.result-row .label{color:#999}
.result-row .value{font-weight:600;color:#1a1a2e}
.result-row .value.green{color:#2d8c6e}
.result-row .value.red{color:#e53935}

/* Scenario toggle */
.scenario-toggle{position:fixed;bottom:20px;right:20px;background:#fff;border:1px solid #e5e5e5;border-radius:8px;padding:8px 16px;font-size:12px;font-weight:600;cursor:pointer;box-shadow:0 2px 12px rgba(0,0,0,0.08);z-index:100;display:flex;align-items:center;gap:8px}
.scenario-toggle .dot{width:8px;height:8px;border-radius:50%}
.scenario-toggle .dot.pass{background:#2d8c6e}
.scenario-toggle .dot.fail{background:#e53935}

/* Hide/show steps */
.kyc-step-content{display:none}
.kyc-step-content.active{display:block}

@media(max-width:900px){
  .kyc-sidebar{display:none}
  .kyc-main{margin-left:0;padding:20px}
}
</style>